/**
 * Centralised BSD & ABSD rate definitions.
 *
 * To update when IRAS changes rates, edit the constants below.
 * All calculators (AffordabilityCalculator, PurchasePlanner, SuperPickerApp)
 * import from this single file.
 *
 * BSD source : https://www.iras.gov.sg/taxes/stamp-duty/for-property/buying-or-acquiring-property/buyer's-stamp-duty-(bsd)
 * ABSD source: https://www.iras.gov.sg/taxes/stamp-duty/for-property/buying-or-acquiring-property/additional-buyer's-stamp-duty-(absd)
 */

// ─── BSD (Buyer's Stamp Duty) ────────────────────────────────────────────────
// Progressive tiers effective 15 Feb 2023.
// Each entry: { ceiling, rate }  — ceiling is the upper bound of the bracket.

export const BSD_TIERS = [
  { ceiling: 180_000, rate: 0.01 },
  { ceiling: 360_000, rate: 0.02 },
  { ceiling: 1_000_000, rate: 0.03 },
  { ceiling: 1_500_000, rate: 0.04 },
  { ceiling: 3_000_000, rate: 0.05 },
  { ceiling: Infinity, rate: 0.06 },
] as const;

/**
 * Calculate Buyer's Stamp Duty for a residential property.
 */
export function calculateBsd(price: number): number {
  if (price <= 0) return 0;
  let duty = 0;
  let prev = 0;
  for (const tier of BSD_TIERS) {
    if (price <= prev) break;
    const taxable = Math.min(price, tier.ceiling) - prev;
    duty += taxable * tier.rate;
    prev = tier.ceiling;
  }
  return Math.round(duty);
}

/**
 * Return the BSD tier breakdown for display / formula dialogs.
 * Each item: { bracket, rate, taxable, duty }.
 */
export function getBsdBreakdown(price: number): {
  bracket: string;
  rate: number;
  taxable: number;
  duty: number;
}[] {
  if (price <= 0) return [];
  const result: { bracket: string; rate: number; taxable: number; duty: number }[] = [];
  let prev = 0;
  for (const tier of BSD_TIERS) {
    if (price <= prev) break;
    const taxable = Math.min(price, tier.ceiling) - prev;
    const pct = Math.round(tier.rate * 100);
    const label =
      tier.ceiling === Infinity
        ? `Above $${(prev / 1000).toLocaleString('en-SG')}k`
        : prev === 0
        ? `First $${(tier.ceiling / 1000).toLocaleString('en-SG')}k`
        : `Next $${((tier.ceiling - prev) / 1000).toLocaleString('en-SG')}k`;
    result.push({
      bracket: `${label} × ${pct}%`,
      rate: tier.rate,
      taxable,
      duty: Math.round(taxable * tier.rate),
    });
    prev = tier.ceiling;
  }
  return result;
}

// ─── ABSD (Additional Buyer's Stamp Duty) ────────────────────────────────────
// Rates effective 27 Apr 2023.

export type CitizenshipType = 'SC' | 'SPR' | 'FOREIGNER';
export type PropertyCount = '1st' | '2nd' | '3rd+';

export const ABSD_RATES: Record<CitizenshipType, Record<PropertyCount, number>> = {
  SC:        { '1st': 0,  '2nd': 20, '3rd+': 30 },
  SPR:       { '1st': 5,  '2nd': 30, '3rd+': 35 },
  FOREIGNER: { '1st': 60, '2nd': 60, '3rd+': 60 },
};

export function getAbsdRate(citizenship: CitizenshipType, propertyCount: PropertyCount): number {
  return ABSD_RATES[citizenship]?.[propertyCount] ?? 0;
}

export function calculateAbsd(price: number, citizenship: CitizenshipType, propertyCount: PropertyCount): number {
  const rate = getAbsdRate(citizenship, propertyCount);
  return Math.round(price * (rate / 100));
}

// ─── FTA ABSD remission (SC-equivalent ABSD rates) ───────────────────────────
// Source: IRAS — Foreigners eligible for ABSD remission under FTAs
// https://www.iras.gov.sg/taxes/stamp-duty/for-property/appeals-refunds-reliefs-and-remissions/common-stamp-duty-remissions-and-reliefs-for-property/foreigners-eligible-for-absd-remission-under-free-trade-agreements-(ftas)
// Legislation: Stamp Duties (Free Trade Agreements) (Remission of ABSD) Rules
//
// Calculator uses SC ABSD rates when the buyer indicates FTA eligibility (Yes).
// Actual remission is applied via e-Stamping / myTax Portal after conditions are met.

/**
 * Detailed nationality rules for the info tooltip (aligned with IRAS FTA ABSD remission).
 * @see https://www.iras.gov.sg/taxes/stamp-duty/for-property/appeals-refunds-reliefs-and-remissions/common-stamp-duty-remissions-and-reliefs-for-property/foreigners-eligible-for-absd-remission-under-free-trade-agreements-(ftas)
 */
export const FTA_ABSD_INFO_TOOLTIP = [
  'Under Singapore’s FTAs, qualifying buyers may apply for ABSD remission so residential stamp duty matches Singapore Citizens (subject to IRAS approval).',
  '',
  'Nationality / status requirements:',
  '• Iceland, Liechtenstein, Norway, or Switzerland — national or Singapore permanent resident',
  '• United States of America — national only (US permanent residents are not eligible under this FTA remission)',
  '',
  'Choose Yes only if you meet the requirement for your country and will apply for remission. Choose No to use standard PR or foreigner ABSD rates.',
].join('\n');

/**
 * For ABSD rate purposes only: when `ftaEligible` is true for SPR/FOREIGNER, use the SC schedule.
 * SC borrowers ignore `ftaEligible`.
 */
export function getEffectiveCitizenshipForAbsd(
  citizenship: CitizenshipType,
  ftaEligible: boolean,
): CitizenshipType {
  if (citizenship === 'SC') return 'SC';
  if (!ftaEligible) return citizenship;
  return 'SC';
}

// ─── ABSD Remission (Married Couples) ─────────────────────────────────────────
// Source: IRAS "ABSD Rates and Remission for Married Couples" (Tables 1–3), effective 27 Apr 2023:
// https://www.iras.gov.sg/docs/default-source/stamp-duty/absd-rates-or-remission-for-joint-purchase-by-a-married-couple_2021.pdf
//
// Joint ABSD is **not** always the SC spouse's individual rate. For example (Table 2, SC–SPR),
// SC with 0 properties and SPR with 1 property before purchase → **30%** ABSD, not 0%.
//
// We map each buyer's "1st / 2nd / ≥3rd property" to **residential properties owned before**
// this purchase: 1st → 0, 2nd → 1, ≥3rd → 2+.

/** Properties owned before this purchase (for IRAS tables). */
function propertyCountToOwnedBefore(pc: PropertyCount): number {
  switch (pc) {
    case '1st':
      return 0;
    case '2nd':
      return 1;
    case '3rd+':
      return 2;
  }
}

function ownedBeforeToPropertyCount(owned: number): PropertyCount {
  if (owned <= 0) return '1st';
  if (owned === 1) return '2nd';
  return '3rd+';
}

/** Table 1: both spouses SC (rates from 27 Apr 2023 column). */
function absdMarriedBothSc(o1: number, o2: number): number {
  const map: Record<string, number> = {
    '0,0': 0,
    '1,0': 20,
    '0,1': 20,
    '1,1': 20,
    '2,0': 30,
    '0,2': 30,
    '2,2': 30,
    '2,1': 30,
    '1,2': 30,
  };
  const k = `${o1},${o2}`;
  if (k in map) return map[k]!;
  return Math.max(
    getAbsdRate('SC', ownedBeforeToPropertyCount(o1)),
    getAbsdRate('SC', ownedBeforeToPropertyCount(o2)),
  );
}

/** Table 2: SC + SPR (rates from 27 Apr 2023 column). Keys are (SC owned before, SPR owned before). */
function absdMarriedScSpr(oSc: number, oSpr: number): number {
  const map: Record<string, number> = {
    '0,0': 0,
    '1,0': 20,
    '0,1': 30,
    '1,1': 30,
    '2,0': 30,
    '0,2': 35,
    '2,2': 35,
    '2,1': 30,
    '1,2': 35,
  };
  const k = `${oSc},${oSpr}`;
  if (k in map) return map[k]!;
  return Math.max(
    getAbsdRate('SC', ownedBeforeToPropertyCount(oSc)),
    getAbsdRate('SPR', ownedBeforeToPropertyCount(oSpr)),
  );
}

/** Table 3: SC + Foreigner (rates from 27 Apr 2023 column). */
function absdMarriedScFr(oSc: number, oFr: number): number {
  const map: Record<string, number> = {
    '0,0': 0,
    '1,0': 60,
    '0,1': 60,
    '1,1': 60,
    '2,0': 60,
    '0,2': 60,
    '2,2': 60,
    '2,1': 60,
    '1,2': 60,
  };
  const k = `${oSc},${oFr}`;
  if (k in map) return map[k]!;
  return Math.max(
    getAbsdRate('SC', ownedBeforeToPropertyCount(oSc)),
    getAbsdRate('FOREIGNER', ownedBeforeToPropertyCount(oFr)),
  );
}

export function getJointAbsdRate(
  citizenship1: CitizenshipType,
  propertyCount1: PropertyCount,
  citizenship2: CitizenshipType,
  propertyCount2: PropertyCount,
  isMarried: boolean,
): { rate: number; remissionApplied: boolean; rateBeforeRemission: number } {
  const rate1 = getAbsdRate(citizenship1, propertyCount1);
  const rate2 = getAbsdRate(citizenship2, propertyCount2);
  const higherRate = Math.max(rate1, rate2);

  if (!isMarried) {
    return { rate: higherRate, remissionApplied: false, rateBeforeRemission: higherRate };
  }

  const o1 = propertyCountToOwnedBefore(propertyCount1);
  const o2 = propertyCountToOwnedBefore(propertyCount2);

  // Table 4: SPR–FR — no ABSD remission for married couples (since 12 Jan 2013).
  if (
    (citizenship1 === 'SPR' && citizenship2 === 'FOREIGNER') ||
    (citizenship1 === 'FOREIGNER' && citizenship2 === 'SPR')
  ) {
    return { rate: higherRate, remissionApplied: false, rateBeforeRemission: higherRate };
  }

  let rate: number;

  if (citizenship1 === 'SC' && citizenship2 === 'SC') {
    rate = absdMarriedBothSc(o1, o2);
  } else if (
    (citizenship1 === 'SC' && citizenship2 === 'SPR') ||
    (citizenship1 === 'SPR' && citizenship2 === 'SC')
  ) {
    const oSc = citizenship1 === 'SC' ? o1 : o2;
    const oSpr = citizenship1 === 'SPR' ? o1 : o2;
    rate = absdMarriedScSpr(oSc, oSpr);
  } else if (
    (citizenship1 === 'SC' && citizenship2 === 'FOREIGNER') ||
    (citizenship1 === 'FOREIGNER' && citizenship2 === 'SC')
  ) {
    const oSc = citizenship1 === 'SC' ? o1 : o2;
    const oFr = citizenship1 === 'FOREIGNER' ? o1 : o2;
    rate = absdMarriedScFr(oSc, oFr);
  } else {
    // No SC (e.g. SPR–SPR), or other pairs not covered by Tables 1–3.
    return { rate: higherRate, remissionApplied: false, rateBeforeRemission: higherRate };
  }

  const remissionApplied = rate < higherRate;
  return { rate, remissionApplied, rateBeforeRemission: higherRate };
}

// ─── Combined helpers ────────────────────────────────────────────────────────

/**
 * Total stamp duty = BSD + ABSD.
 */
export function calculateTotalStampDuty(
  price: number,
  absdRatePercent: number = 0,
): number {
  const bsd = calculateBsd(price);
  const absd = absdRatePercent > 0 ? Math.round(price * (absdRatePercent / 100)) : 0;
  return bsd + absd;
}

/**
 * Outlay = Down Payment + BSD.  (Used by super-picker unit table.)
 */
export function calculateOutlay(price: number, loanToValueRatio: number): number {
  if (price <= 0) return 0;
  const downPaymentPct = Math.max(0, Math.min(1, 1 - loanToValueRatio));
  const downPayment = price * downPaymentPct;
  return downPayment + calculateBsd(price);
}

// ─── Display labels ──────────────────────────────────────────────────────────

export const CITIZENSHIP_LABELS: Record<CitizenshipType, string> = {
  SC: 'Singaporean',
  SPR: 'P.R',
  FOREIGNER: 'Foreigner',
};

export const PROPERTY_COUNT_OPTIONS: { value: PropertyCount; label: string }[] = [
  { value: '1st', label: '1st Property' },
  { value: '2nd', label: '2nd Property' },
  { value: '3rd+', label: '≥ 3rd Property' },
];
